import { Check } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface ApproveApplicationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (approverName: string) => void | Promise<void>;
	isPending?: boolean;
}

const ApproveApplicationModal: React.FC<ApproveApplicationModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	isPending = false,
}) => {
	const [approverName, setApproverName] = useState("");

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			setApproverName("");
			onClose();
		}
	};

	const handleConfirm = async () => {
		if (!approverName.trim()) return;
		await onConfirm(approverName.trim());
		// Parent closes modal on success via onClose; on failure we stay open for retry
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Approve Application</DialogTitle>
				</DialogHeader>
				<DialogDescription>
					By approving this application, you confirm that all information has
					been reviewed and meets requirements.
				</DialogDescription>
				<div className="space-y-1.5">
					<Label htmlFor="approver-name">Your Name (Required):</Label>
					<Input
						id="approver-name"
						type="text"
						placeholder="Enter your full name"
						value={approverName}
						onChange={(e) => setApproverName(e.target.value)}
						disabled={isPending}
					/>
				</div>
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" disabled={isPending}>
							Cancel
						</Button>
					</DialogClose>
					<Button
						onClick={handleConfirm}
						disabled={!approverName.trim() || isPending}
					>
						{isPending ? (
							"Approving..."
						) : (
							<>
								<Check size={16} />
								Confirm Approval
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ApproveApplicationModal;
